import React, { useMemo } from 'react';
import { View, ViewStyle, StyleProp, TextStyle } from 'react-native';
import MyAppText from './MyAppText';
import { TextInputMask } from 'react-native-masked-text';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../services/translate';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

type MyCpfInputProps = {
    viewStyle?: StyleProp<ViewStyle>;
    required?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    disabled?: boolean;
} & ({
    value: string;
    onChangeText: (value: string) => void;
    isInvalid: boolean;
    setIsInvalid: (value: boolean) => void;
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: never;
    onChangeText?: never;
    isInvalid?: never;
    setIsInvalid?: never;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
})

export default function MyCpfInput({ value, onChangeText, viewStyle, isInvalid, setIsInvalid, required, form, fieldName, customValidations, pattern, labelStyle, disabled }: MyCpfInputProps) {
    const { styles, theme } = useStyles(stylesheet);

    function invalidCpf(cpf: string) {
        if (cpf === '' || cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
            return true;
        }

        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let checkDigit1 = 11 - (sum % 11);
        if (checkDigit1 >= 10) {
            checkDigit1 = 0;
        }
        if (checkDigit1 !== parseInt(cpf.charAt(9))) {
            return true;
        }

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf.charAt(i)) * (11 - i);
        }
        let checkDigit2 = 11 - (sum % 11);
        if (checkDigit2 >= 10) {
            checkDigit2 = 0;
        }
        if (checkDigit2 !== parseInt(cpf.charAt(10))) {
            return true;
        }

        return false;
    }

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        if (pattern) {
            rules.pattern = pattern;
        }

        rules.validate = {
            validateCpf: (value) => !invalidCpf(value) || translate('invalidDocument')
        };

        if (customValidations) {
            rules.validate = {
                ...rules.validate,
                ...customValidations
            };
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[{ rowGap: 5 }, viewStyle]}>
                    <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'flex-start' }}>
                        <MyAppText style={labelStyle ? labelStyle : styles.filterText}>CPF</MyAppText>
                        {required ?
                            <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                            : null}
                    </View>
                    <TextInputMask
                        onBlur={field.onBlur}
                        value={field.value}
                        includeRawValueInChangeText={true}
                        onChangeText={(maskedText, rawText) => {
                            field.onChange(rawText || '');
                        }}
                        type={'cpf'}
                        style={[styles.filterInput(field.value == '', fieldState.invalid),
                            disabled && styles.disabled
                        ]}
                        placeholder='000.000.000-00'
                        editable={!disabled}
                    />
                    {isInvalid && value !== '' ?
                        <MyAppText style={styles.error}>{translate('invalidDocument')}</MyAppText> : null}
                    {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;

    } else if (value !== undefined && onChangeText !== undefined) {
        const isPlaceholder = useMemo(() => !value && value.length === 0, [value]);
        return (
            <View style={[{ rowGap: 5 }, viewStyle]}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'flex-start' }}>
                    <MyAppText style={labelStyle ? labelStyle : styles.filterText}>CPF</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <TextInputMask
                    value={value}
                    includeRawValueInChangeText={true}
                    onChangeText={(maskedText, rawText) => {
                        onChangeText(rawText || '');
                        if (rawText) {
                            setIsInvalid(invalidCpf(rawText));
                        }
                    }}
                    type={'cpf'}
                    style={[styles.filterInput(isPlaceholder, isInvalid && value !== ''),
                        disabled && styles.disabled
                    ]}
                    placeholder='000.000.000-00'
                    editable={!disabled}
                />
                {isInvalid && value !== '' ?
                    <MyAppText style={styles.error}>{translate('invalidDocument')}</MyAppText> : null}

            </View>
        );
    }
}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: {
        flex: 1,
        minWidth: 200,
    },
    filterText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },

    filterInput: (isPlaceholder: boolean, isInvalid: boolean) => ({
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderWidth: 1,
        borderRadius: 4,
        borderColor: isInvalid ? '#FF0000' : theme.colors.field.border,
        height: 40,
        minHeight: 40,
        fontSize: 16,
        color: isPlaceholder ? theme.colors.placeholder : theme.colors.field.text,
        padding: 10
    }),
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    },
    error: {
        color: '#FF0000',
        fontSize: 10
    },
}));
